/**
 * Shared table class names for the operator lists.
 *
 * There are six tables in this app (clients, cohorts, the cohort's members,
 * students, a client's staff, license usage) and every one had its own copy of
 * the row classes. One definition instead, so a styling decision lands
 * everywhere at once — and so the next table added does not start a seventh
 * variant.
 *
 * Two readability affordances on long tables, both requested during
 * PROV-COHORT review:
 *
 * - **Hover** (`surface-card-hover`) tells you which row your pointer is on.
 *   On a nine-column roster that is the difference between reading across
 *   the right row and reading across the one below it.
 * - **Zebra striping** (`surface-card-inset` on even rows) does the same job
 *   for the eye without a pointer, when scanning down a column.
 *
 * Both use tokens that already exist in `apps/web/tailwind.config.ts` —
 * `surface-card-inset` and `surface-card-hover` were defined for exactly
 * this and were simply unused here. No new token, no hard-coded hex.
 *
 * Hover is listed AFTER the zebra classes so it wins on an even row;
 * Tailwind emits them in that order, and reversing it makes hover invisible
 * on half the table.
 */

/**
 * Wrap every table in this.
 *
 * A table's min-content width is the sum of its columns, and a nine-column
 * roster exceeds the space left beside the filter rail — so without a scroll
 * container the table simply overflows and paints ON TOP of the rail.
 * `min-w-0` is the other half: a flex child defaults to `min-width:auto`,
 * which means "at least my content", so the column refuses to shrink and
 * `overflow-x-auto` never engages.
 *
 * Scrolling the table inside its own column, rather than letting the page
 * scroll sideways, keeps the rail and the nav where they are.
 */
export const TABLE_WRAP = 'min-w-0 overflow-x-auto'

/** The `<table>` element itself. */
export const TABLE = 'w-full bg-surface-card rounded-token-card overflow-hidden text-table'

/** Keep column headings on one line; TABLE_WRAP owns horizontal overflow. */
export const TABLE_HEAD_ROW =
  'text-left whitespace-nowrap border-b border-border-default'

/**
 * A body row: separator, zebra stripe, hover highlight.
 *
 * `last:border-0` because the table has `overflow-hidden` and a trailing
 * border reads as a stray line against the rounded corner.
 */
export const TABLE_ROW =
  // The shared class is what lets ROW_LINK stretch across the real table row.
  'sc-list-row-link-row border-b border-border-default last:border-0 ' +
  'even:bg-surface-card-inset hover:bg-surface-card-hover'

/**
 * A body row in a table nested inside a card that is ALREADY
 * `surface-card` — the cohort's member list, a client's staff. Same
 * hover, no stripe: two nested surfaces striping against each other reads
 * as a rendering fault rather than a pattern.
 */
export const TABLE_ROW_NESTED =
  'border-b border-border-default last:border-0 hover:bg-surface-card-hover'

/**
 * The row's PRIMARY link: clicking anywhere in the row follows it.
 *
 * A stretched `::after` over the whole row, rather than an `onClick` on the
 * `<tr>`. That distinction is the entire point — this stays a real `<a>`, so
 * cmd/middle-click opens a new tab, right-click offers "Copy link", the
 * status bar previews the target, and it is reachable and activatable by
 * keyboard. A div with a click handler has none of that.
 *
 * (Wrapping `<tr>` in an `<a>` is not an option: invalid HTML inside a
 * table, and nested anchors are invalid anywhere.)
 *
 * Requires `TABLE_ROW` (for `relative`) on the row. Use on exactly ONE link
 * per row — two stretched overlays would make whichever is painted last win
 * arbitrarily.
 */
export const ROW_LINK = 'sc-list-row-link underline'

/**
 * Anything else interactive in a row that has a ROW_LINK: a link to a
 * DIFFERENT destination, a checkbox, a button.
 *
 * Lifts it above the stretched overlay so it receives its own clicks. Without
 * this the row link swallows them and the control looks broken — the
 * failure is silent, which is why it belongs in a named constant rather than
 * an inline `z-10` someone has to remember.
 */
export const ROW_LINK_ABOVE = 'sc-list-row-link-above'
